import { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import type { TravelerId } from "../types";
import type { TripStore } from "../hooks/useTripData";
import { findPinForScheduleItem } from "./PlacePhoto";
import { DayPanel } from "./DayPanel";
import "../styles/quest-atlas.css";
type Track = { name: string; segments: number[][][] };
const KEY = "chile-atlas-tracks-v1";
function loadTracks(): Record<string, Track> {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(([, v]) => {
        const t = v as Track;
        return (
          t &&
          typeof t.name === "string" &&
          Array.isArray(t.segments) &&
          t.segments.every(
            (s) =>
              Array.isArray(s) &&
              s.length > 1 &&
              s.every(
                (p) =>
                  Array.isArray(p) &&
                  p.length === 2 &&
                  Number.isFinite(p[0]) &&
                  Number.isFinite(p[1]) &&
                  Math.abs(p[0]) <= 180 &&
                  Math.abs(p[1]) <= 90,
              ),
          )
        );
      }),
    ) as Record<string, Track>;
  } catch {
    return {};
  }
}
export function QuestAtlas({
  store,
  date,
  onSelectDate,
  voterId,
  selectedItemId,
  onSelectItem,
}: {
  store: TripStore;
  date: string;
  onSelectDate: (date: string) => void;
  voterId: TravelerId;
  selectedItemId: string | null;
  onSelectItem: (id: string) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [tracks, setTracks] = useState(loadTracks);
  const [message, setMessage] = useState("");
  const [selectedPin, setSelectedPin] = useState("");
  const [overview, setOverview] = useState(false);
  const day = store.getDay(date) ?? store.data.days[0];
  const pins = useMemo(
    () =>
      overview
        ? store.data.pins
        : store.data.pins.filter(
            (p) =>
              p.dayIds?.includes(day.date) ||
              (day.items.some((i) => i.category === "trek") &&
                p.category === "lodging" &&
                p.dayIds?.includes(
                  store.data.days[store.data.days.indexOf(day) - 1]?.date,
                )),
          ),
    [overview, store.data.pins, store.data.days, day],
  );
  const isHikingDay = day.items.some((i) => i.category === "trek");
  const track = isHikingDay ? tracks[day.date] : undefined;
  useEffect(() => {
    const receive = (e: MessageEvent) => {
      if (
        e.origin !== location.origin ||
        e.source !== frame.current?.contentWindow
      )
        return;
      if (e.data?.type === "atlas-ready") setReady(true);
      if (e.data?.type === "atlas-place") setSelectedPin(e.data.name);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);
  useEffect(() => {
    if (ready)
      frame.current?.contentWindow?.postMessage(
        {
          type: "trip-day",
          pins: pins.length || track ? pins : store.data.pins,
          track: overview ? null : track,
        },
        location.origin,
      );
  }, [ready, pins, track, overview, store.data.pins]);
  useEffect(() => {
    setSelectedPin("");
    setMessage("");
    document
      .getElementById("quest-date-" + date)
      ?.scrollIntoView({
        block: "nearest",
        inline: "center",
        behavior: "smooth",
      });
  }, [date]);
  async function importTrack(file: File) {
    try {
      if (file.size > 5_000_000)
        throw Error("Choose a GPX file smaller than 5 MB.");
      const xml = new DOMParser().parseFromString(
        await file.text(),
        "application/xml",
      );
      if (xml.querySelector("parsererror"))
        throw Error("This GPX file could not be read.");
      let segments = Array.from(xml.querySelectorAll("trkseg")).map((s) =>
        Array.from(s.querySelectorAll("trkpt")),
      );
      if (!segments.length)
        segments = [Array.from(xml.querySelectorAll("rtept"))];
      const coordinates = segments
        .map((s) =>
          s.map((p) => {
            const lat = Number(p.getAttribute("lat")),
              lon = Number(p.getAttribute("lon"));
            if (
              !p.hasAttribute("lat") ||
              !p.hasAttribute("lon") ||
              !Number.isFinite(lat) ||
              !Number.isFinite(lon) ||
              Math.abs(lat) > 90 ||
              Math.abs(lon) > 180
            )
              throw Error("The GPX contains invalid coordinates.");
            return [lon, lat];
          }),
        )
        .filter((s) => s.length > 1);
      if (!coordinates.length)
        throw Error("No usable track or route was found.");
      const next = {
        ...tracks,
        [day.date]: { name: file.name, segments: coordinates },
      };
      localStorage.setItem(KEY, JSON.stringify(next));
      setTracks(next);
      setOverview(false);
      setMessage(
        "Trail added to this day. GPX tracks are stored separately on this device.",
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save this track.");
    }
  }
  useEffect(() => {
    if (!ready || overview || !selectedItemId) return;
    const item = day.items.find((i) => i.id === selectedItemId);
    const pin = item && findPinForScheduleItem(item, pins, store.data.pins);
    if (pin)
      frame.current?.contentWindow?.postMessage(
        { type: "atlas-focus", id: pin.id },
        location.origin,
      );
  }, [ready, overview, selectedItemId, day, pins, store.data.pins]);
  const done = day.todos.filter((t) => t.done).length;
  return (
    <section className="quest-atlas">
      <nav className="quest-calendar" aria-label="Trip calendar">
        {store.data.days.map((d, i) => (
          <button
            id={"quest-date-" + d.date}
            key={d.date}
            className={d.date === day.date ? "active" : ""}
            aria-pressed={d.date === day.date}
            onClick={() => {
              onSelectDate(d.date);
              setOverview(false);
            }}
          >
            <small>{format(parseISO(d.date), "EEE")}</small>
            <strong>{format(parseISO(d.date), "d")}</strong>
            <span>
              {format(parseISO(d.date), "MMM")} · Day {i + 1}
            </span>
            <i>{d.location || "Travel"}</i>
          </button>
        ))}
      </nav>
      <div className="quest-workspace">
        <div className="quest-map">
          <button
            className="btn quest-overview-toggle"
            aria-pressed={overview}
            onClick={() => setOverview(!overview)}
          >
            {overview ? "Return to this day" : "View whole trip"}
          </button>
          <iframe
            ref={frame}
            title="Trip discovery map"
            src="/atlas-demo/?embedded=1"
          />
          <div className="quest-map-caption">
            <strong>
              {overview ? "Your whole journey" : day.location || day.title}
            </strong>
            <span>
              {pins.length} mapped places ·{" "}
              {track && !overview
                ? "Imported GPX trail"
                : "Select a photo to discover a place"}
            </span>
            {selectedPin && <b>{selectedPin}</b>}
          </div>
          {!pins.length && !track && (
            <div className="quest-map-empty">
              No places mapped for this day yet. The trip overview remains
              visible.
            </div>
          )}
        </div>
        <aside className="quest-day">
          <p className="eyebrow">
            {format(parseISO(day.date), "EEEE, d MMMM")} · DAILY QUESTS
          </p>
          <h3>{day.title}</h3>
          <p>{day.summary}</p>
          <div className="quest-progress">
            <span>Preparation</span>
            <strong>
              {done} / {day.todos.length} complete
            </strong>
          </div>
          <progress value={done} max={day.todos.length || 1} />
          {isHikingDay && (
            <section className="quest-route">
              <strong>
                {track ? "Your hiking trail" : "Add this day’s hiking trail"}
              </strong>
              <p>
                {track
                  ? track.name
                  : "Import a GPX from your route provider to see the actual path on the map."}
              </p>
              <label className="btn quest-upload">
                {track ? "Replace GPX" : "Import GPX"}
                <input
                  aria-label="Import GPX for selected day"
                  type="file"
                  accept=".gpx,application/gpx+xml"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void importTrack(f);
                    e.target.value = "";
                  }}
                />
              </label>
              {track && (
                <button
                  className="btn"
                  onClick={() => {
                    const next = { ...tracks };
                    delete next[day.date];
                    try {
                      localStorage.setItem(KEY, JSON.stringify(next));
                      setTracks(next);
                      setMessage("Trail removed from this day.");
                    } catch {
                      setMessage("Could not remove the saved track.");
                    }
                  }}
                >
                  Remove trail
                </button>
              )}
              <small>
                Imported geometry; trail access and conditions are not verified.
              </small>
              {message && <p role="status">{message}</p>}
            </section>
          )}
          <DayPanel
            compact
            showMap={false}
            voterId={voterId}
            selectedItemId={selectedItemId}
            onSelectItem={onSelectItem}
            date={day.date}
            store={store}
            onSelectDate={onSelectDate}
          />
        </aside>
      </div>
    </section>
  );
}
